'use client';

import { HStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/styled/text';
import { type TraceToolCall } from '../../chat/trace-view/types';
import { useArtifactsOptional } from '../../chat/trace-view/ArtifactsProvider';
import { parseTerminalResult } from '../../chat/components/hitl-types';
import { extractCommandFromArtifactContent } from '../../chat/components/tools/terminal/terminal-input-parser';
import { CliEditor } from './CliEditor';
import { AddToEvidenceButton } from '../components/AddToEvidenceButton';

type CliArtifactProps = {
  toolCall: TraceToolCall;
  displayName: string;
};

type ResolvedCliData = {
  command: string | undefined;
  result:
    | {
        stdout?: string;
        stderr?: string;
        exitCode?: number;
      }
    | undefined;
  duration: number | undefined;
  hasResult: boolean;
  isCompleted: boolean;
  artifactId: string | undefined;
  isEvidence: boolean;
};

function extractCommand(toolCall: TraceToolCall): string | undefined {
  if (typeof toolCall.args.command === 'string') {
    return toolCall.args.command;
  }
  if (typeof toolCall.args.cmd === 'string') {
    return toolCall.args.cmd;
  }
  return undefined;
}

function parseCliResult(output: string | undefined) {
  if (!output) {
    return undefined;
  }

  const trimmedOutput = output.trim();
  if (!trimmedOutput.startsWith('{') && !trimmedOutput.startsWith('[')) {
    return parseTerminalResult(output);
  }

  return parseTerminalResult(
    (() => {
      try {
        return JSON.parse(output) as unknown;
      } catch (error) {
        console.warn('Failed to parse CLI result as JSON', error);
        return output;
      }
    })(),
  );
}

function useResolvedCliArtifact(toolCall: TraceToolCall): ResolvedCliData {
  const artifactsContext = useArtifactsOptional();
  const artifact = artifactsContext?.getArtifactByToolCallId(toolCall.id);
  const command =
    extractCommandFromArtifactContent(artifact?.actionContent) ??
    extractCommand(toolCall);
  const terminalResult = parseCliResult(
    artifact?.resultContent ?? toolCall.output,
  );
  const duration =
    artifact?.durationMs ??
    terminalResult?.duration ??
    toolCall.timing?.durationMs;
  const isCompleted = toolCall.status === 'completed';
  const hasResult =
    Boolean(command) &&
    (Boolean(artifact?.resultContent) ||
      Boolean(toolCall.output) ||
      Boolean(toolCall.error) ||
      duration !== undefined);

  return {
    command,
    result: terminalResult
      ? {
          stdout: terminalResult.stdout,
          stderr: terminalResult.stderr ?? toolCall.error,
          exitCode: terminalResult.exitCode,
        }
      : toolCall.error
        ? { stderr: toolCall.error, exitCode: 1 }
        : undefined,
    duration,
    hasResult,
    isCompleted,
    artifactId: artifact?.id ?? artifact?.runtimeId ?? undefined,
    isEvidence: artifactsContext?.isEvidence(artifact) ?? false,
  };
}

export function CliArtifact({ toolCall, displayName }: CliArtifactProps) {
  const {
    command,
    result,
    duration,
    hasResult,
    isCompleted,
    artifactId,
    isEvidence,
  } = useResolvedCliArtifact(toolCall);
  const headerActions = isCompleted ? (
    <AddToEvidenceButton
      artifactId={artifactId}
      label="Add as evidence"
      isEvidence={isEvidence}
    />
  ) : null;

  const showShimmer = !hasResult;
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;

  const toolNameText = (
    <Text
      fontSize="xs"
      color="fg.muted"
      animationStyle={showShimmer ? 'shimmer' : undefined}
      style={shimmerStyle}
    >
      {displayName}
    </Text>
  );

  if (!hasResult) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        {toolNameText}
      </HStack>
    );
  }

  if (!command) {
    return null;
  }

  return (
    <CliEditor
      command={command}
      result={result}
      duration={duration}
      isEvidence={isEvidence}
      headerActions={headerActions}
    />
  );
}
